// Slide 6 - Certgen
import { ExternalLink } from "lucide-react";

export default function Slide6() {
  const images = [
    "/certgen/certgen_1.png",
    "/certgen/certgen_2.png",
    "/certgen/certgen_3.png",
  ];

  return (
    <section className="w-full max-w-[1400px] relative z-10">
      <div className="absolute -right-16 -top-12 w-72 h-72 bg-gradient-to-br from-indigo-800/20 to-sky-700/10 rounded-full blur-3xl pointer-events-none opacity-60 hidden md:block" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10 items-center">
        {/* Left: Content (glass card) */}
        <div className="self-center flex flex-col space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5 p-3 sm:p-4 md:p-6 lg:p-8 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-white/6 via-white/3 to-transparent backdrop-blur-xl border border-white/12 shadow-2xl relative overflow-hidden group hover:border-white/20 transition-all duration-500 max-w-xl">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/4 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-gradient-to-r from-white/10 to-white/4 rounded-full text-white text-xs sm:text-sm uppercase tracking-wider font-bold border border-white/20 shadow-lg">
                Product
              </span>
            </div>

            <div className="mt-2 sm:mt-3 md:mt-4 lg:mt-6">
              <h2
                className="text-white font-black leading-none tracking-tight drop-shadow-lg"
                style={{ fontSize: "clamp(32px,6vw,60px)" }}
              >
                CertGen
              </h2>
              <div className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-white/10 rounded-lg mt-1 sm:mt-2">
                <span className="text-white/70 uppercase tracking-widest text-[10px] sm:text-xs md:text-sm font-bold">
                  Certificate generator
                </span>
              </div>
            </div>

            <p className="text-white/90 text-sm sm:text-base md:text-base lg:text-lg leading-5 sm:leading-6 md:leading-7 mt-2 sm:mt-3 md:mt-4 lg:mt-6">
              Modern <span className="keyword">certificate generation</span>{" "}
              service — <span className="keyword">templated PDFs</span>,{" "}
              <span className="keyword">secure verification</span>, and a{" "}
              <span className="keyword">lightweight CMS</span> for managing
              certificate templates and content.
            </p>

            <div className="h-px bg-white/6 my-2 sm:my-3 md:my-4 lg:my-6" />

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-white/70 text-xs sm:text-sm md:text-sm font-semibold">
                Setup in
              </div>
              <span className="relative flex items-center">
                <span className="absolute inline-flex h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-3.5 md:w-3.5 rounded-full bg-white/60 opacity-75 animate-ping" />
                <span className="relative inline-block h-1.5 w-1.5 sm:h-2 sm:w-2 md:h-2.5 md:w-2.5 rounded-full bg-white shadow" />
              </span>
              <div className="text-white font-medium text-sm sm:text-base md:text-base">10 mins</div>
            </div>

            <div className="mt-2 sm:mt-3 md:mt-4 lg:mt-6">
              <a
                href="https://certgen.org"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 sm:gap-2 bg-white text-gray-900 px-3 sm:px-4 md:px-5 lg:px-6 py-1.5 sm:py-2 md:py-2.5 rounded-full font-semibold text-sm sm:text-base shadow-md transition-transform hover:scale-105 border border-white/20"
              >
                Visit Website
                <ExternalLink size={12} className="sm:w-3 sm:h-3 md:w-4 md:h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Right: Images column */}
        {/* Right: Gallery - large image with two smaller below (match Slide4 layout) */}
        <div className="relative h-[640px] hidden lg:flex items-start justify-center">
          <div className="w-full max-w-[880px]">
            {/* Large hero image */}
            <div className="mb-2 sm:mb-3 md:mb-4">
              <figure className="bg-white/6 p-2 sm:p-3 rounded-2xl sm:rounded-3xl border border-white/10 shadow-2xl transition-transform duration-300">
                <img
                  src={images[0]}
                  alt={`CertGen large screenshot`}
                  className="w-full rounded-lg object-contain bg-white/4"
                  style={{ aspectRatio: "16/10" }}
                />
              </figure>
            </div>

            {/* Two smaller images below */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
              <figure className="bg-white/6 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl border border-white/10 shadow-lg transition-transform duration-300">
                <img
                  src={images[1]}
                  alt={`CertGen screenshot 2`}
                  className="w-full rounded-lg object-cover"
                  style={{ aspectRatio: "16/9" }}
                />
              </figure>
              <figure className="bg-white/6 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl border border-white/10 shadow-lg transition-transform duration-300">
                <img
                  src={images[2]}
                  alt={`CertGen screenshot 3`}
                  className="w-full rounded-lg object-cover"
                  style={{ aspectRatio: "16/9" }}
                />
              </figure>
            </div>
          </div>
        </div>

        {/* Mobile: stacked large image then two smaller */}
        <div className="flex flex-col gap-2 sm:gap-3 md:gap-4 lg:hidden">
          <div className="bg-white/6 p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-2xl">
            <img
              src={images[0]}
              alt={`CertGen large`}
              className="w-full rounded-lg object-contain bg-white/4"
              style={{ aspectRatio: "16/10" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
            <div className="bg-white/6 p-1.5 sm:p-2 rounded-lg sm:rounded-xl shadow-2xl">
              <img
                src={images[1]}
                alt={`CertGen screenshot 2`}
                className="w-full rounded-lg object-cover"
                style={{ aspectRatio: "16/9" }}
              />
            </div>
            <div className="bg-white/6 p-1.5 sm:p-2 rounded-lg sm:rounded-xl shadow-2xl">
              <img
                src={images[2]}
                alt={`CertGen screenshot 3`}
                className="w-full rounded-lg object-cover"
                style={{ aspectRatio: "16/9" }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
